import { useEffect, useState } from "react";
import { Button, Spin, Typography } from "antd";
import { BulbOutlined, LoadingOutlined, ReloadOutlined } from "@ant-design/icons";
import { useAiWidget, useRegenerateAiWidget, useWidgetData } from "../Contexts/dashboardHooks";
import { useAiAccess } from "../useAiAccess";
import { AiWidgetParts } from "./AiWidgetParts";
import "@styles/styles-AiInsightsWidget.css";

const { Text } = Typography;

/** Seconds left on a 429 window, re-rendered each second while it runs. */
function useThrottleCountdown(retryAt: number | null): number {
  const [secondsLeft, setSecondsLeft] = useState(() => remaining(retryAt));

  useEffect(() => {
    setSecondsLeft(remaining(retryAt));
    if (retryAt == null) return;
    const timer = setInterval(() => {
      const left = remaining(retryAt);
      setSecondsLeft(left);
      if (left <= 0) clearInterval(timer);
    }, 1000);
    return () => clearInterval(timer);
  }, [retryAt]);

  return secondsLeft;
}

const remaining = (retryAt: number | null): number =>
  retryAt == null ? 0 : Math.max(0, Math.ceil((retryAt - Date.now()) / 1000));

/**
 * A read-only chat turn in a dashboard tile: the coach's one question, answered
 * by the V2 chatbot and rendered with the chat page's own components. Regenerate
 * is the only interaction.
 */
export function AiInsightsWidget({ id }: { id: string }) {
  // The widget's own processed data is just its spec — `process` passes it through.
  const processed = useWidgetData(id);
  const spec = processed?.type === "ai_insights" ? processed.spec : undefined;
  const hasAccess = useAiAccess();
  const ai = useAiWidget(id);
  const regenerate = useRegenerateAiWidget(id);
  const secondsLeft = useThrottleCountdown(ai?.retryAt ?? null);

  const query = spec?.query?.trim() ?? "";
  const isStreaming = ai?.status === "loading" || ai?.status === "streaming";
  const throttled = secondsLeft > 0;
  const parts = ai?.parts ?? [];

  return (
    <div className="ai-insights" data-grid-id={id}>
      <div className="ai-insights__header">
        <div className="ai-insights__header-left">
          <BulbOutlined className="ai-insights__header-icon" />
          <span className="ai-insights__header-title">AI Insights</span>
          {hasAccess && query !== "" && (
            <Button
              type="text"
              size="small"
              title={throttled ? `Try again in ${secondsLeft}s` : "Regenerate"}
              icon={<ReloadOutlined spin={isStreaming} />}
              disabled={isStreaming || throttled}
              onClick={regenerate}
              className="ai-insights__refresh-btn"
            />
          )}
        </div>
      </div>

      <div className="ai-insights__body">
        {!hasAccess ? (
          <div className="ai-insights__placeholder">
            <Text type="secondary">
              DataViz AI isn't enabled for this team. Contact your account manager to turn it on.
            </Text>
          </div>
        ) : query === "" ? (
          <div className="ai-insights__placeholder">
            <Text type="secondary">
              Open this widget's settings and ask a question about your dashboard.
            </Text>
          </div>
        ) : (
          <>
            {parts.length > 0 && <AiWidgetParts parts={parts} />}

            {ai?.preview && parts.length === 0 && (
              <div className="ai-insights__preview">{ai.preview}</div>
            )}

            {isStreaming && (
              <div className="ai-insights__pending">
                {/* Turns run up to 90s and every phase boundary emits a frame, so the
                    step label shows progress. The spinner matches WidgetShell's. */}
                <Spin indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />} />
                <Text type="secondary" className="ai-insights__step">
                  {ai?.step ?? "Thinking…"}
                </Text>
              </div>
            )}

            {ai?.error && (
              <div className="ai-insights__error">
                <Text type="secondary">
                  {ai.error}
                  {throttled && ` Try again in ${secondsLeft}s.`}
                </Text>
              </div>
            )}

            {ai == null && (
              <div className="ai-insights__placeholder">
                <Text type="secondary">Waiting for dashboard data…</Text>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
